import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { exhibitionsApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import {
  Plus, Loader2, Sparkles, Calendar, MapPin, ChevronRight, PhoneCall, Power,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import Header from '../components/layout/Header';

const Exhibitions = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    name: '', city: '', venue: '', start_date: '', end_date: '', active: true,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await exhibitionsApi.list();
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load exhibitions');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Dates required'); return; }
    if (form.end_date < form.start_date) { toast.error('End date must be after start'); return; }
    setSaving(true);
    try {
      const r = await exhibitionsApi.create(form);
      toast.success(`"${r.data.name}" created`);
      setCreateOpen(false);
      setForm({ name: '', city: '', venue: '', start_date: '', end_date: '', active: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const toggleActive = async (e, ex) => {
    e.stopPropagation();
    try {
      await exhibitionsApi.update(ex.id, { active: !ex.active });
      setItems(prev => prev.map(x => x.id === ex.id ? { ...x, active: !ex.active } : x));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed');
    }
  };

  const active = items.filter(x => x.active);
  const past = items.filter(x => !x.active);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-neutral-950" data-testid="exhibitions-page">
      <Header title="Exhibition+" />
      <div className="max-w-screen-xl mx-auto px-3 md:px-8 py-4 md:py-6 space-y-5">
        {/* Title + Create */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <Sparkles className="h-5 w-5 text-amber-700 dark:text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg md:text-xl font-bold tracking-tight">Exhibitions</h2>
              <p className="text-xs md:text-sm text-muted-foreground">
                Create a branded selection sheet and WhatsApp it to your customer in one tap
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white h-10"
            data-testid="new-exhibition-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Exhibition
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-amber-600" /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 rounded-2xl bg-white dark:bg-neutral-900 border border-dashed">
            <Sparkles className="h-10 w-10 mx-auto mb-3 text-amber-500" />
            <p className="text-sm font-semibold">No exhibitions yet</p>
            <p className="text-xs text-muted-foreground mt-1 mb-4">
              Create your first exhibition — takes 30 seconds.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCreateOpen(true)}
              data-testid="empty-new-exhibition-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Create Exhibition
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <ExhibitionSection title="Active" items={active} onToggle={toggleActive} navigate={navigate} testid="active" />
            {past.length > 0 && (
              <ExhibitionSection title="Past / Archived" items={past} onToggle={toggleActive} navigate={navigate} testid="past" muted />
            )}
          </div>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={(v) => !saving && setCreateOpen(v)}>
        <DialogContent className="max-w-lg" data-testid="create-exhibition-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-amber-600" /> New Exhibition
            </DialogTitle>
            <DialogDescription>Sales staff will pick this from a dropdown while creating selections.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. IIJS Premiere 2026"
                className="h-11 mt-1"
                data-testid="ex-name"
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">City</Label>
                <Input
                  value={form.city}
                  onChange={e => setForm(p => ({ ...p, city: e.target.value }))}
                  placeholder="Mumbai"
                  className="h-11 mt-1"
                  data-testid="ex-city"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Venue</Label>
                <Input
                  value={form.venue}
                  onChange={e => setForm(p => ({ ...p, venue: e.target.value }))}
                  placeholder="BEC"
                  className="h-11 mt-1"
                  data-testid="ex-venue"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Start date *</Label>
                <Input
                  type="date"
                  value={form.start_date}
                  onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))}
                  className="h-11 mt-1"
                  data-testid="ex-start"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">End date *</Label>
                <Input
                  type="date"
                  value={form.end_date}
                  onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))}
                  className="h-11 mt-1"
                  data-testid="ex-end"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="submit-exhibition"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ExhibitionSection = ({ title, items, onToggle, navigate, testid, muted }) => (
  <div className={cn(muted && 'opacity-90')}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-2">
      {title} <span className="ml-1 opacity-70">{items.length}</span>
    </p>
    <div className="rounded-2xl bg-white dark:bg-neutral-900 border overflow-hidden">
      <div className="divide-y">
        {items.map(ex => (
          <button
            key={ex.id}
            type="button"
            onClick={() => navigate(`/exhibitions/${ex.id}`)}
            className="w-full text-left px-3 md:px-5 py-3.5 hover:bg-amber-50/40 dark:hover:bg-amber-500/5 transition-colors flex items-center gap-3 flex-wrap sm:flex-nowrap"
            data-testid={`exhibition-card-${ex.id}`}
          >
            <div className={cn("h-11 w-11 rounded-lg flex items-center justify-center flex-shrink-0",
              ex.active ? "bg-amber-100 dark:bg-amber-500/20" : "bg-slate-100 dark:bg-neutral-800")}>
              <Sparkles className={cn("h-4 w-4", ex.active ? "text-amber-700 dark:text-amber-400" : "text-slate-500")} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold truncate">{ex.name}</p>
                {ex.active ? (
                  <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">Active</Badge>
                ) : (
                  <Badge variant="outline" className="text-[10px]">Archived</Badge>
                )}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-muted-foreground mt-0.5 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> {ex.dates_label}</span>
                {(ex.city || ex.venue) && (
                  <span className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {[ex.city, ex.venue].filter(Boolean).join(' · ')}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <PhoneCall className="h-3 w-3" /> {ex.selection_count || 0} selection{ex.selection_count === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={(e) => onToggle(e, ex)}
              className={cn("p-2 rounded-md hover:bg-muted flex-shrink-0",
                ex.active ? "text-emerald-600" : "text-slate-400")}
              title={ex.active ? "Archive" : "Reactivate"}
              data-testid={`toggle-active-${ex.id}`}
            >
              <Power className="h-4 w-4" />
            </button>
            <ChevronRight className="h-5 w-5 text-muted-foreground/30 flex-shrink-0" />
          </button>
        ))}
      </div>
    </div>
  </div>
);

export default Exhibitions;
